import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import Ic from "./icons.jsx";

const GAP = 6;
const MARGIN = 8;
const MAX_WIDTH = 380;
const CLOSE_DELAY_MS = 120;

/**
 * A popover with free text, anchored to a small trigger (Strahinja, 2026-10-09).
 * Hover opens it for a mouse; a tap or click pins it open on a tablet or phone
 * (hard rule 12: nothing lives on hover alone); Escape, a press outside or a
 * second tap closes it. The popover is a fixed portal, so a scrolling table
 * wrapper or a modal can never clip it; it flips above the trigger when there
 * is no room below and never leaves the viewport sideways.
 */
function useAnchoredPopover(text: string) {
  const id = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<number | null>(null);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [pos, setPos] = useState<CSSProperties | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelClose();
    setOpen(false);
    setPinned(false);
  }, [cancelClose]);

  /** Hover leaves with a short grace period, so the mouse can travel into the popover. */
  const closeSoon = useCallback(() => {
    if (pinned) return;
    cancelClose();
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  }, [pinned, cancelClose]);

  useEffect(() => cancelClose, [cancelClose]);

  // Anchor the fixed popover to the trigger; follow scroll/resize of any ancestor.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    function place() {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;
      const width = Math.min(MAX_WIDTH, viewportW - 2 * MARGIN);
      const left = Math.max(MARGIN, Math.min(r.left, viewportW - width - MARGIN));
      const below = viewportH - r.bottom - GAP;
      const above = r.top - GAP;
      const flip = below < 160 && above > below;
      setPos(flip
        ? { position: "fixed", left, width, bottom: viewportH - r.top + GAP, maxHeight: Math.max(80, above - MARGIN) }
        : { position: "fixed", left, width, top: r.bottom + GAP, maxHeight: Math.max(80, below - MARGIN) });
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // Pinned (tapped / clicked): Escape and a press outside close it.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { e.stopPropagation(); close(); }
    }
    function onPointerDown(e: PointerEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || popRef.current?.contains(t)) return;
      close();
    }
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [open, close]);

  function toggle() {
    cancelClose();
    if (open && pinned) { close(); return; }
    setOpen(true);
    setPinned(true);
  }

  const popover: ReactNode = open && pos && (
    // The mouse handlers only hold the popover open while it is hovered; the trigger button is the interactive element.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={popRef}
      id={id}
      role="tooltip"
      className="text-pop"
      style={pos}
      onMouseEnter={cancelClose}
      onMouseLeave={closeSoon}
    >
      {text}
    </div>
  );

  /** Props for the trigger button; the caller adds its own class, label and content. */
  const triggerProps = {
    ref: triggerRef,
    type: "button" as const,
    "aria-describedby": open ? id : undefined,
    "aria-expanded": open,
    onMouseEnter: () => { cancelClose(); setOpen(true); },
    onMouseLeave: closeSoon,
    onClick: toggle,
    onFocus: () => setOpen(true),
    onBlur: () => { if (!pinned) setOpen(false); },
  };

  return { open, triggerProps, popover: popover ? createPortal(popover, document.body) : null };
}

/**
 * Long free text in a narrow table cell (a note): the cell shows the first few
 * words on one line, the whole text opens in the popover.
 */
export function TextPopover({ text, className, style }: {
  text: string;
  /** Extra class on the clipped trigger (the cell already constrains its width). */
  className?: string;
  style?: CSSProperties;
}) {
  const { open, triggerProps, popover } = useAnchoredPopover(text);
  return (
    <>
      <button {...triggerProps} className={`text-clip${open ? " is-open" : ""}${className ? ` ${className}` : ""}`} style={style}>
        {text}
      </button>
      {popover}
    </>
  );
}

/**
 * A small "i" button next to a label (an attribute with a note on the asset /
 * position form and card, Strahinja 2026-10-09): the note opens in the popover.
 */
export function InfoPopover({ text, label }: {
  text: string;
  /** What the note is about — read out as "Napomena: <label>". */
  label: string;
}) {
  const { t } = useTranslation();
  const { open, triggerProps, popover } = useAnchoredPopover(text);
  return (
    <>
      <button {...triggerProps} className={`info-btn${open ? " is-open" : ""}`} aria-label={t("common.noteAbout", "Napomena: {{label}}", { label })}>
        <Ic.info aria-hidden="true" />
      </button>
      {popover}
    </>
  );
}
